export type RunTraceLevel = 'silent' | 'summary' | 'verbose';

export const RUN_TRACE_LEVEL_OPTIONS: Array<{ value: RunTraceLevel; label: string }> = [
  { value: 'silent', label: 'Minimal' },
  { value: 'summary', label: 'Summary' },
  { value: 'verbose', label: 'Verbose' }
];

const asString = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

const eventIdFrom = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value).toString();
  }
  return null;
};

export const summarizeRunEvent = (eventType: string, payload: Record<string, unknown>): string | null => {
  if (eventType === 'progress') {
    const stage = asString(payload.stage);
    const detail = asString(payload.detail);
    const percent = typeof payload.percent === 'number' ? ` ${Math.round(payload.percent)}%` : '';
    const summary = [stage, detail].filter(Boolean).join(': ');
    return `${summary}${percent}` || 'Working…';
  }
  if (eventType === 'status') {
    const detail = asString(payload.detail);
    const status = asString(payload.status);
    return detail || status || null;
  }
  if (eventType === 'error') {
    return asString(payload.detail) || asString(payload.message) || asString(payload.error) || 'Run failed';
  }
  if (eventType === 'trace') {
    return asString(payload.text) || null;
  }
  if (eventType === 'plan') {
    return asString(payload.title) || 'Plan updated';
  }
  if (eventType === 'plan_step') {
    const status = asString(payload.status);
    const detail = asString(payload.detail);
    const stepId = asString(payload.stepId);
    return detail || [stepId, status].filter(Boolean).join(' · ') || 'Plan step updated';
  }
  if (eventType === 'artifact') {
    return asString(payload.summary) || 'Artifact ready';
  }
  if (eventType === 'artifact_update') {
    return asString(payload.summary) || 'Artifact updated';
  }
  return null;
};

export interface RunActivityEntry {
  id: string;
  eventType: string;
  summary: string;
}

export const buildRunActivityEntry = (
  eventType: string,
  payload: Record<string, unknown>,
  eventId?: string
): RunActivityEntry | null => {
  const summary = summarizeRunEvent(eventType, payload);
  if (!summary) {
    return null;
  }
  return {
    id:
      eventIdFrom(eventId) ??
      eventIdFrom(payload.eventId) ??
      `${eventType}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    eventType,
    summary
  };
};

export const appendRunActivity = (
  current: RunActivityEntry[],
  entry: RunActivityEntry,
  limit = 18
): RunActivityEntry[] => {
  const next = [...current.filter((item) => item.id !== entry.id), entry];
  if (next.length <= limit) {
    return next;
  }
  return next.slice(next.length - limit);
};
